<template>
    <router-link
        :to="vacancy.is_our_vacancy ? '' : `/vacancy/${vacancy.id}`"
        @click.prevent="openVacancy"
        class="vacancy-item"
        :class="{ 'our-vacancy': vacancy.is_our_vacancy }"
    >
  <div class="vacancy-item">

    <div
        v-if="vacancy.is_our_vacancy"
        class="our-vacancy-badge"
      >
        ✨ Это ваша вакансия
      </div>

    <div class="vacancy-header">
      <img class="company-logo" :src="vacancy.company_logo_url || '/public/default-logo.png'" alt="Лого компании" />
      <div class="vacancy-info">
        <h3 class="company-name">{{ vacancy.company_name ? vacancy.company_name : "Не указано" }}</h3>
        <p class="specific">{{ vacancy.company_specifics ? vacancy.company_specifics : "Не указано" }}</p>
      </div>

      <button
        type="button"
        class="like-button"
        :class="{ loading: isLikeLoading }"
        :disabled="isLikeLoading"
        :aria-label="
          isLiked
            ? 'Удалить из избранного'
            : 'Добавить в избранное'
        "
        @click.stop.prevent="toggleLike"
      >
        <img
          :src="
            isLiked
              ? '/like_active.png'
              : '/like_not_active.png'
          "
          alt=""
          class="like-icon"
        />

        <span
          v-if="isLikeLoading"
          class="like-spinner"
        ></span>
      </button>

    </div>

    <div class="image-container">

        <div class="city-label">
          {{ cityLabel }}
        </div>

        <div v-if="loading" class="spinner-overlay">
          <div class="spinner"></div>
        </div>

        <img
        v-if="vacancy.image_url"
        :src="vacancy.image_url"
        alt="Вакансия"
        class="vacancy-image"
        :class="{ loaded: !loading }"
        @load="loading = false"
      />
      </div>

    <div class="preview-container">
      <img v-if="vacancy.image_url" :src="vacancy.image_url" alt="Превью" class="preview-image" />
      <div class="text-overlay">
        <h3 class="vacancy-title" ref="titleRef" :class="`title-lines-${descriptionLineClamp}`">
          {{ vacancy.title || "Название вакансии" }}
        </h3>
          <p
            class="vacancy-description"
            :class="`lines-${descriptionLineClamp}`"
          >
            {{ vacancy.info || "Описание вакансии. Здесь будет краткое описание в 3-4 строки." }}
          </p>
      </div>
    </div>
  </div>
</router-link>
</template>

<script setup>
import { ref, watchEffect, defineEmits, computed, nextTick } from "vue";

const isLikeLoading = ref(false);
const props = defineProps({ vacancy: Object });
const loading = ref(true);

const emit = defineEmits([
  "toggle-favorite",
  "open-vacancy",
  "open-our-vacancy"
]);

const theme = ref(localStorage.getItem("theme") || "dark");
const isLiked = computed(() => props.vacancy?.is_liked ?? false);
const titleRef = ref(null);
const descriptionLineClamp = computed(() => {
  const len = props.vacancy?.title?.length || 0;
  return len > 45 ? 2 : 3;
});

const openVacancy = () => {
  if (!props.vacancy) {
    console.error(
      "❌ Ошибка: vacancy не определена!"
    );

    return;
  }

  if (props.vacancy.is_our_vacancy) {
    emit(
      "open-our-vacancy",
      props.vacancy
    );

    return;
  }

  emit(
    "open-vacancy",
    props.vacancy
  );
};

const toggleLike = () => {
  if (isLikeLoading.value) {
    return;
  }

  if (navigator.vibrate) {
    navigator.vibrate(50);
  }

  const raw =
    props.vacancy?.id ??
    props.vacancy?.vacancyID ??
    props.vacancy?.vacancy_id;

  const vacancyId =
    typeof raw === "object"
      ? (
          raw?.id ??
          raw?.vacancyID ??
          raw?.vacancy_id ??
          ""
        )
      : String(raw ?? "");

  if (!vacancyId) {
    return;
  }

  isLikeLoading.value = true;

  emit("toggle-favorite", {
    vacancyId,

    done: () => {
      isLikeLoading.value = false;
    }
  });
};

const regions = {
  1: "Москва",
  2: "Санкт-Петербург",
};

const getCityName = (id) => {
  return regions[Number(id)] || "Регион не указан";
};

const cityLabel = computed(() => {
  if (props.vacancy?.is_remote) return "Удалёнка";
  return getCityName(props.vacancy?.city_id);
});

watchEffect(() => {
  theme.value = document.documentElement.getAttribute("data-theme") || "dark";
});

watchEffect(() => {
  if (titleRef.value && props.vacancy?.title) {
    nextTick(() => {
      const el = titleRef.value;
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
      const height = el.offsetHeight;

      const isTwoLines = height >= lineHeight * 1.8;
      descriptionLineClamp.value = isTwoLines ? 2 : 3;

      console.log("🎯 title height:", height, "→ lineClamp:", descriptionLineClamp.value);
    });
  }
});


</script>

<style scoped>

:root {
  --vacancy-bg-dark: #373A3F;
  --vacancy-bg-light: #D8DCE2;
  --vacancy-border-dark: rgba(255, 255, 255, 0.6);
  --vacancy-border-light: rgba(0, 0, 0, 0.4);
  --company-name-dark: white;
  --company-name-light: black;
  --specific-dark: #838383;
  --specific-light: #BCBCBC;
}

.vacancy-item {
  cursor: pointer;
  width: 375px;
  height: 328px;
  border-radius: 17px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  overflow: hidden;
  position: relative;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, background-color 0.2s ease-in-out, border 0.2s ease-in-out;
  border: 0.1px solid rgb(58, 58, 58);
}

[data-theme="light"] .vacancy-item {
  background-color: white;
  border: 0.1px solid rgb(225, 225, 225);
}

[data-theme="dark"] .vacancy-item {
  background:
    linear-gradient(
      145deg,
      #22242a 0%,
      #1d1f24 100%
    );

  border:
    1px solid
    rgba(255, 255, 255, 0.07);

  box-shadow:
    0
    16px
    40px
    rgba(0, 0, 0, 0.18);
}

.vacancy-item:hover {
  transform: scale(1.01);
  box-shadow: 0px 7px 14px rgba(0, 0, 0, 0.2);
}

.vacancy-header {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px;
}

.company-logo {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  margin-left: 8px;
  margin-right: 8px;
  object-fit: cover;
  box-shadow: 0px 7px 14px rgba(171, 171, 171, 0.2);
}

.vacancy-info {
  display: flex;
  flex-direction: column;
}

.company-name {
  font-size: 12px;
  font-family: "Eastman-Medium", sans-serif;
  color: var(--company-name-dark);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.3px;
}

[data-theme="light"] .company-name {
  color: var(--company-name-light);
}

.specific {
  font-size: 10px;
  font-family: "Eastman-Regular", sans-serif;
  color: var(--specific-dark);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

[data-theme="light"] .specific {
  color: var(--specific-light);
}

.image-container {
  width: 100%;
  height: 170px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.city-label {
  position: absolute;
  top: 10px;
  right: 16px;
  padding: 3px 14px;
  font-size: 10px;
  font-family: "Eastman-Medium", sans-serif;
  color: white;
  border-radius: 18px;
  background: linear-gradient(90deg, #2276E6, #8247E2);
}

.vacancy-image {
  width: 100%;
  height: 170px;
  object-fit: cover;
  opacity: 1;
  transition: opacity 0.3s ease-in-out;
}

.vacancy-image.loaded {
  opacity: 1;
}

.preview-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
}

.preview-image {
  width: 110%;
  height: 110%;
  object-fit: cover;
  filter: blur(10px) brightness(0.3);
  opacity: 0.9;
  position: absolute;
  top: -5%;
  left: -5%;
}

.text-overlay {
  position: absolute;
  top: 12px;
  left: 12px;
  width: calc(100% - 24px);
  color: white;
  display: flex;
  flex-direction: column;
}

.vacancy-title {
  margin-left: 2px;
  font-size: 14px;
  font-family: "Eastman", sans-serif;
  margin-bottom: 0px;
  margin-top: 0px;
  color: rgb(238, 238, 238);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.2; 
}

.vacancy-description {
  font-size: 9px;
  font-family: "Eastman-Regular", sans-serif;
  line-height: 1.3;
  max-height: 4em;
  overflow: hidden;
  color: rgb(219, 219, 219);
  margin-bottom: 0px;
  margin-top: 4px;
  margin-left: 2px;
  margin-right: 7px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  -webkit-line-clamp: 3; /* 👈 ДОБАВЬ ЭТО */
}

.lines-2 {
  -webkit-line-clamp: 2;
}

.lines-3 {
  -webkit-line-clamp: 3;
}

.like-button {
  position: absolute;

  right: 14px;

  width: 28px;
  height: 28px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  background: transparent;

  border: none;

  border-radius: 50%;

  cursor: pointer;

  appearance: none;
  -webkit-appearance: none;

  transition:
    transform 0.2s ease,
    opacity 0.2s ease;
}


.like-button:hover:not(:disabled) {
  transform: scale(1.12);
}


.like-button:active:not(:disabled) {
  transform: scale(0.92);
}


.like-button:disabled {
  cursor: default;
}


/* ============================================================
   HEART
============================================================ */

.like-icon {
  width: 18px;
  height: 16px;

  display: block;

  object-fit: contain;

  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}


.like-button.loading
.like-icon {
  opacity: 0.12;

  transform:
    scale(0.72);
}


/* ============================================================
   LOADER
============================================================ */

.like-spinner {
  position: absolute;

  width: 17px;
  height: 17px;

  box-sizing: border-box;

  border:
    2px solid
    rgba(55, 127, 228, 0.16);

  border-top-color:
    #377fe4;

  border-radius: 50%;

  animation:
    like-spinner-rotation
    0.65s
    linear
    infinite;
}


@keyframes like-spinner-rotation {
  to {
    transform: rotate(360deg);
  }
}


/* ============================================================
   DARK
============================================================ */

[data-theme="dark"]
.like-spinner {
  border-color:
    rgba(255, 255, 255, 0.10);

  border-top-color:
    #aeb8c6;
}

.like-button:active {
  transform: scale(0.9);
}

.spinner-overlay {
  position: absolute;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 170px;
  background-color: rgba(0, 0, 0, 0.15);
}

.spinner {
  width: 28px;
  height: 28px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top: 3px solid white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.title-lines-1 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
}

.title-lines-2 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

.like-button:hover {
  transform: scale(1.2);
  filter: drop-shadow(0px 0px 4px rgba(255,255,255,0.4));
  opacity: 0.9;
}

.vacancy-item,
.vacancy-item:link,
.vacancy-item:visited,
.vacancy-item:hover,
.vacancy-item:active {
  text-decoration: none;
  color: inherit;
}

.our-vacancy {
  opacity: .68;
  filter: saturate(.75);
}

.our-vacancy:hover {
  opacity: .85;
}

.our-vacancy::after {
  content: "";
  position: absolute;
  inset: 0;
  background: rgba(255,255,255,.18);
  pointer-events: none;
}

[data-theme="dark"] .our-vacancy::after {
  background: rgba(0,0,0,.28);
}


.our-vacancy-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 30;

  padding: 6px 12px;

  border-radius: 999px;

  background: linear-gradient(
    135deg,
    #636363,
    #ababab
  );

  color: white;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 10px;

  box-shadow:
    0 6px 18px rgba(59,130,246,.35);
}

</style>